import React, { useMemo, useState } from "react";

import "./BitcoinRebalancingHistory.scss";
import AddIcon from "@mui/icons-material/Add";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import RemoveIcon from "@mui/icons-material/Remove";
import moment from "moment";

import FallbackTickerIcon from "../common/FallbackTickerIcon/FallbackTickerIcon";
import { BN_TICKER_META, stripInlineMarkdown } from "./constants";

const pct = (value, withSign = false) =>
  `${withSign && value > 0 ? "+" : ""}${(value * 100).toFixed(2)}%`;

/**
 * Rebalancing timeline: each rebalance is a collapsible dropdown headed by a
 * sticky date strip. Every date is expanded by default; open days divide into
 * "N TICKERS TO ADJUST DOWN / UP / HOLD"; each row opens the ticker detail
 * drawer via View Details (or a row click).
 */
const BitcoinRebalancingHistory = ({ portfolioData, onViewTicker }) => {
  const rebalances = useMemo(
    () =>
      [...(portfolioData?.adjustments || [])].sort(
        (a, b) =>
          new Date(b.rebalance_date).getTime() -
          new Date(a.rebalance_date).getTime(),
      ),
    [portfolioData],
  );

  // Every date is expanded by default; each strip collapses independently.
  const [collapsed, setCollapsed] = useState(new Set());
  const toggle = (date) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });

  if (rebalances.length === 0) {
    return (
      <div className="bn-rebalancing-history__empty">
        No rebalancing history yet.
      </div>
    );
  }

  const renderRow = (position) => {
    const isNew = position.oldW === 0 && position.newW !== 0;
    return (
      <div
        key={position.ticker}
        onClick={() => onViewTicker(position.ticker)}
        className="bn-rebalancing-history__row"
      >
        <div className="bn-rebalancing-history__row-ticker">
          <FallbackTickerIcon
            ticker={position.ticker}
            className="bn-rebalancing-history__row-logo"
            src={`https://assets.tradealgo.com/stock-logos/${position.ticker}.png`}
          />
          <span className="bn-rebalancing-history__row-symbol">
            {position.ticker}
          </span>
          {isNew && (
            <span className="bn-rebalancing-history__new-pill">New</span>
          )}
        </div>

        <div className="bn-rebalancing-history__row-sector">
          {position.sector || "—"}
        </div>

        <div className="bn-rebalancing-history__row-weights">
          <span className="bn-rebalancing-history__row-weight old">
            {(position.oldW * 100).toFixed(2)}%
          </span>
          <ArrowRightAltIcon
            fontSize="small"
            className="bn-rebalancing-history__row-arrow"
          />
          <span className="bn-rebalancing-history__row-weight">
            {(position.newW * 100).toFixed(2)}%
          </span>
          <span className="bn-rebalancing-history__diff-pill">
            {pct(position.diff, true)}
          </span>
        </div>

        <button
          onClick={(event) => {
            event.stopPropagation();
            onViewTicker(position.ticker);
          }}
          className="bn-rebalancing-history__view-details"
        >
          View Details
          <ArrowOutwardIcon fontSize="small" />
        </button>
      </div>
    );
  };

  const renderGroup = (label, rows, colorClass) =>
    rows.length > 0 && (
      <div className="bn-rebalancing-history__group">
        <div className={`bn-rebalancing-history__group-title ${colorClass}`}>
          {rows.length} TICKER{rows.length !== 1 ? "S" : ""} {label}
        </div>
        <div>{rows.map(renderRow)}</div>
      </div>
    );

  return (
    <div className="bn-rebalancing-history">
      {rebalances.map((rebalance) => {
        const positions = Object.entries(rebalance.adjustments || {}).map(
          ([ticker, adjustment]) => ({
            ticker,
            sector: adjustment.sector || BN_TICKER_META[ticker]?.sector || "",
            oldW: adjustment.old,
            newW: adjustment.new,
            diff: adjustment.diff,
          }),
        );
        const byMagnitude = (a, b) => Math.abs(b.diff) - Math.abs(a.diff);
        const down = positions
          .filter((position) => position.diff < 0)
          .sort(byMagnitude);
        const up = positions
          .filter((position) => position.diff > 0)
          .sort(byMagnitude);
        const hold = positions
          .filter((position) => position.diff === 0)
          .sort((a, b) => b.newW - a.newW);
        const isOpen = !collapsed.has(rebalance.rebalance_date);

        return (
          <div key={rebalance.rebalance_date}>
            <button
              onClick={() => toggle(rebalance.rebalance_date)}
              className="bn-rebalancing-history__date-strip"
            >
              <span className="bn-rebalancing-history__date">
                {moment(rebalance.rebalance_date).format("dddd, MMM D, YYYY")}
                <span className="bn-rebalancing-history__count">
                  {positions.length}
                </span>
              </span>
              {isOpen ? (
                <RemoveIcon className="bn-rebalancing-history__collapse-icon" />
              ) : (
                <AddIcon className="bn-rebalancing-history__collapse-icon" />
              )}
            </button>

            {isOpen && (
              <div className="bn-rebalancing-history__body">
                {rebalance.reason && (
                  <div className="bn-rebalancing-history__reason">
                    <AutoFixHighIcon
                      sx={{ fontSize: 16 }}
                      className="bn-rebalancing-history__reason-icon"
                    />
                    <p>{stripInlineMarkdown(rebalance.reason)}</p>
                  </div>
                )}
                {renderGroup("TO ADJUST DOWN", down, "down")}
                {renderGroup("TO ADJUST UP", up, "up")}
                {renderGroup("TO HOLD", hold, "hold")}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BitcoinRebalancingHistory;
